define((require) => {
    "use strict";

    const ImageWidgetModel                 = require("shared/ui/experience/widgets/imageWidget/ImageWidgetModel");
    const Size                             = require("shared/util/Size");
    const NavigationWidgetTargets            = require("ui/editor/NavigationWidgetTargets").default;

    /**
     * @class {ImageWidgetModelEditorMixin}
     *
     * Extra classes used by the editor, but not needed by the viewer.
     */
    return ImageWidgetModel.mixin("ImageWidgetModelEditorMixin", {
        // A list of properties that are not meant to be persisted;
        transient: [
            "selectedWidgetId",
            "cropEnabled",
            "isDragAvailable",
            "isCurrentWidgetCroppingUnavailable",
            // I believe the above options has pretty much been eliminated.
            "canCrop",
            "isCroppable"
        ],

        /**
         * 1) Resize (both grow or shrink) the image to fit it inside an experience,
         * 2) reset the rotation of the image,
         * 3) move the image back inside the scene boundaries, if needed
         *
         * This method is called when clicking the 'fit to scene' button.
         * @param {ExperienceModel} experience The experience the images is to be fitted in to.
         */
        fitToScene(experience) {
            const percentageSize = this._getOriginalPercentageSize(experience);

            if (percentageSize === null) {
                return;
            }

            this._setSize(this._getFittedSize(percentageSize), experience);
            this._resetRotation();
            this._setFittedWidgetPosition();
        },

        /**
         * Set the size of the image relative to the size of the scene. If the image is larger than
         * the experience it is shunk to fit. The method is called when a new image is dragged on
         * to the scene or an image is assigned to an empty image placeholder.
         *
         * @param {ExperienceModel} experience The experience the images is placed in.
         */
        initializeSizeOfImage(experience) {
            const percentageSize = this._getOriginalPercentageSize(experience);

            if (percentageSize === null) {
                return;
            }

            const imageIsLargerThanExperience = percentageSize.width > 100 || percentageSize.height > 100;

            if (imageIsLargerThanExperience) {
                this._setSize(this._getFittedSize(percentageSize), experience);
            } else {
                this._setSize(percentageSize, experience);
            }
        },

        setSizeOfImage(experience, width, height, imageWidth, imageHeight) {
            const size = new Size(((width) * imageWidth), ((height) * imageHeight));

            if (size === null) {
                return;
            }
            this._setSize(size, experience);
        },

        /**
         * Set the position of the cropped image to the same as before crop.
         * This method prevents crop part of image jumping to the start of original image.
         * The method is called when a crop action has been applied to the image.
         *
         * @param {Crop} crop The crop object properties of the image.
         * @param {number} prevWidth The image width before crop
         * @param {number} prevHeight The image height before crop
         */
        setPositionOfCroppedImage(crop, prevWidth, prevHeight, imageSizes, experience, fullImageOffsetX, fullImageOffsetY) {
            const top = this.get("top");
            const left = this.get("left");
            // Calculate new position
            // of the cropped image piece
            let cropLeft;
            let cropTop;
            if (imageSizes) {
                // Recalculate cropped image position in canvas
                const fullImagePercentWidth = imageSizes.width / 100; // Percent image width size in pixels
                const fullImagePercentHeight = imageSizes.height / 100; // Percent image height size in pixels
                const imageOffsetXSceneRelative = fullImageOffsetX / (experience.get("width") / 100); // Image X offset relative relative to scene
                const imageOffsetYSceneRelative = fullImageOffsetY / (experience.get("height") / 100); // Image Y offset relative relative to scene
                // Calculate cropped image position
                const croppedAreaOffsetXSceneRelative = (fullImagePercentWidth * crop.x * 100) / (experience.get("width") / 100);
                const croppedAreaOffsetYSceneRelative = (fullImagePercentHeight * crop.y * 100) / (experience.get("height") / 100);
                // Set positions
                cropLeft = imageOffsetXSceneRelative + croppedAreaOffsetXSceneRelative;
                cropTop = imageOffsetYSceneRelative + croppedAreaOffsetYSceneRelative;
            } else {
                cropLeft = left +  (prevWidth * crop.x);
                cropTop = top + (prevHeight * crop.y);
            }
            this.set("left", cropLeft);
            this.set("top", cropTop);
        },

        _scaleImage() {
            const maxWidth = this.get("unscaledWidgetWidth");
            const maxHeight = this.get("unscaledWidgetHeight");

            const originalImageWidth = this.get("originalImageWidth");
            const originalImageHeight = this.get("originalImageHeight");

            const scaledSize = this._calculateAspectRatioFit(originalImageWidth, originalImageHeight, maxWidth, maxHeight);
            return scaledSize;
        },

        _calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
            let ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
            return {width: srcWidth * ratio, height: srcHeight * ratio};
        },

        /**
         * Returns the size of the ORIGINAL image source in percentages of the size of the experience.
         *
         * @param {ExperienceModel} experience The experience that the image is sized relative to.
         * @return {Size} The size of the image in percentages of the size of the scene.
         */
        _getOriginalPercentageSize(experience) {
            return experience.getPercentageSize(this._getOriginalImageSize());
        },

        /**
         * Returns the size of the image in percentages of the size of the experience.
         *
         * @param {ExperienceModel} experience The experience that the image is sized relative to.
         * @return {Size} The size of the image in percentages of the size of the scene.
         */
        _getPercentageSize(experience, width, height) {
            return experience.getPercentageSize(this._getImageSize(width, height));
        },

        /**
         * Returns the original size of the associated image.
         *
         * @return {Size} The original size of the associated image, potentially falling back
         *                on the widget size to be backwards compatible with older image widget, i.e. no
         *                <tt>originalImageWidth</tt> or <tt>originalImageHeight</tt> defined.
         */

        _getOriginalImageSize() {
            // Scales the widget down to a fixed size while also preserving the aspect ratio
            const isImageLargerThanWidget = (this.get("originalImageWidth") > this.get("unscaledWidgetWidth") || this.get("originalImageHeight") > this.get("unscaledWidgetHeight"));
            if (this.get("scaleOnInitialize") && isImageLargerThanWidget) {
                const scaledSize = this._scaleImage();
                return Size.from(scaledSize);
            }

            if (this.containsImageSize()) {
                return Size.from(this.get("originalImageWidth"), this.get("originalImageHeight"));
            }

            return Size.from(this.get("width"), this.get("height"));
        },

        _getImageSize(width, height) {
            return Size.from(width, height);
        },

        /**
         * Returns self description string
         *
         * @return {string|null} The self description
         */
        _getSelfDescription: function() {
            // First - to check if this is previous or next scene navigation arrow
            const isNavigationArrows = this.get("action") && this.get("action").get("type") === "sceneNavigation" && !this.get("src");
            if (isNavigationArrows) {
                // Previous/Next scene navigation
                const actionTarget = this.get("action").get("target");
                if (actionTarget === NavigationWidgetTargets.PREVIOUS_SCENE || actionTarget === NavigationWidgetTargets.NEXT_SCENE) {
                    return `${this.get("action").get("target").charAt(0).toUpperCase()}${this.get("action").get("target").slice(1)} Scene`;
                }
                return `Go To Scene ${this.get("action").get("target")}`;
            }
            // Image
            return this.get("altText") && this.get("altText").length > 0
                ? `Image: ${this.get("altText")}`
                : null;
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/imageWidget/ImageWidgetModelEditorMixin.js